import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react'
import { Button } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleCheck, faCircleExclamation, faXmark } from '@fortawesome/free-solid-svg-icons'

interface ResultMessage {
  id: number
  success: boolean
  message: string
}

const ResultToastContext = createContext<(success: boolean, message: string) => void>(() => {})

export function useResultToast() {
  return useContext(ResultToastContext)
}

export function ResultToastProvider({ children }: { children: React.ReactNode }) {
  const [messages, setMessages] = useState<ResultMessage[]>([])
  const timeouts = useRef<Record<number, number>>({})
  const nextId = useRef(1)

  const remove = useCallback((id: number) => {
    const timeout = timeouts.current[id]
    if (timeout) {
      window.clearTimeout(timeout)
      delete timeouts.current[id]
    }
    setMessages((current) => current.filter((message) => message.id !== id))
  }, [])

  const notify = useCallback((success: boolean, message: string) => {
    const id = nextId.current++
    setMessages((current) => [...current, { id, success, message }])
    timeouts.current[id] = window.setTimeout(() => remove(id), success ? 3200 : 5200)
  }, [remove])

  const value = useMemo(() => notify, [notify])

  return (
    <ResultToastContext.Provider value={value}>
      {messages.length > 0 && (
        <div className="global-notification-stack" aria-live="polite" aria-atomic="false">
          {messages.map((message) => (
            <div key={message.id} className={`global-notification global-notification--${message.success ? 'success' : 'danger'}`}>
              <div className="global-notification__icon">
                <FontAwesomeIcon icon={message.success ? faCircleCheck : faCircleExclamation} />
              </div>
              <div className="global-notification__content">
                <div className="global-notification__title">{message.success ? 'Saved' : 'Attention needed'}</div>
                <div className="global-notification__message">{message.message}</div>
              </div>
              <Button type="button" variant="link" className="global-notification__close" onClick={() => remove(message.id)} aria-label="Dismiss notification">
                <FontAwesomeIcon icon={faXmark} />
              </Button>
            </div>
          ))}
        </div>
      )}
      {children}
    </ResultToastContext.Provider>
  )
}
